import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";

type DocumentType = Database["public"]["Enums"]["document_type"];

export type EntityType = 
  | "vehicle" 
  | "property" 
  | "subcontractor" 
  | "health" 
  | "driver"
  | "legal"
  | "demarche_document";

export interface DocConfig {
  key: string;
  label: string;
  doc_type?: string;
  validityDays?: number | null;
  keywords?: string[];
}

interface UseDocumentLinkOptions {
  entityType: EntityType;
  entityId: string;
  docConfig: DocConfig;
  linkTableName: string;
  open: boolean;
  onSuccess?: () => void;
  familyMemberId?: string;
}

export interface ExistingDocument {
  id: string;
  title: string;
  file_name: string;
  file_path: string;
  mime_type: string | null;
  bucket_id: string;
  doc_type: string;
  issuer_name: string | null;
  doc_date: string | null;
  expires_at: string | null;
  ai_summary: string | null;
  category: string | null;
  key_fields: any;
  created_at: string;
}

interface ExtractedDates {
  validFrom: string | null;
  expiresAt: string | null;
  issueDate: string | null;
}

// Map entity types to their link table configurations
const ENTITY_CONFIG: Record<EntityType, {
  idColumn: string;
  typeColumn: string;
  storagePath: string;
}> = {
  vehicle: {
    idColumn: "vehicle_id",
    typeColumn: "doc_type",
    storagePath: "fleet",
  },
  property: {
    idColumn: "property_id",
    typeColumn: "doc_type",
    storagePath: "properties",
  },
  subcontractor: {
    idColumn: "subcontractor_id",
    typeColumn: "document_type",
    storagePath: "subcontractors",
  },
  health: {
    idColumn: "user_id",
    typeColumn: "category_key",
    storagePath: "health",
  },
  driver: {
    idColumn: "driver_id",
    typeColumn: "doc_type",
    storagePath: "drivers",
  },
  legal: {
    idColumn: "company_id",
    typeColumn: "document_type",
    storagePath: "legal",
  },
  demarche_document: {
    idColumn: "id",
    typeColumn: "doc_type",
    storagePath: "demarches",
  },
};

export function useDocumentLink({
  entityType,
  entityId,
  docConfig,
  linkTableName,
  open,
  onSuccess,
  familyMemberId,
}: UseDocumentLinkOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDoc, setSelectedDoc] = useState<ExistingDocument | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [isExtractingDates, setIsExtractingDates] = useState(false);
  const [datesExtracted, setDatesExtracted] = useState(false);
  const [isExtractingLinkDates, setIsExtractingLinkDates] = useState(false);
  const [linkDatesExtracted, setLinkDatesExtracted] = useState(false);
  
  const extractionAbortRef = useRef<AbortController | null>(null);

  const config = ENTITY_CONFIG[entityType];

  // Extract dates from a file stored in the backend storage using AI
  const extractDatesFromStorageFile = useCallback(async (params: {
    filePath: string;
    fileName: string;
    mimeType: string;
  }): Promise<ExtractedDates> => {
    try {
      console.log("[useDocumentLink] Calling extract-document-info", {
        filePath: params.filePath,
        fileName: params.fileName,
        mimeType: params.mimeType,
      });

      const { data, error } = await supabase.functions.invoke("extract-document-info", {
        body: {
          fileName: params.fileName,
          mimeType: params.mimeType,
          filePath: params.filePath,
          // Let backend auto-detect; it will still extract doc_date/expires_at
        },
      });

      if (error) {
        console.error("[useDocumentLink] extract-document-info error:", error);
        throw error;
      }

      const result: ExtractedDates = {
        validFrom: null,
        expiresAt: null,
        issueDate: null,
      };

      if (data) {
        // doc_date is used across the app
        if (data.doc_date) {
          result.issueDate = data.doc_date;
          result.validFrom = data.doc_date;
        }

        // Some flows may return issue_date too
        if (!result.validFrom && data.issue_date) {
          result.issueDate = data.issue_date;
          result.validFrom = data.issue_date;
        }

        if (data.expires_at) {
          result.expiresAt = data.expires_at;
        }
      }

      return result;
    } catch (error) {
      console.error("[useDocumentLink] Date extraction failed:", error);
      return { validFrom: null, expiresAt: null, issueDate: null };
    }
  }, []);

  // Extract dates from newly selected upload file (by temporary upload)
  const extractDatesFromFile = useCallback(async (uploadFile: File): Promise<ExtractedDates> => {
    if (!user?.id) return { validFrom: null, expiresAt: null, issueDate: null };

    const fileExt = uploadFile.name.split(".").pop();
    const tempPath = `temp/${user.id}/${Date.now()}.${fileExt}`;

    // Upload temporarily to storage
    const { error: uploadError } = await supabase.storage
      .from("documents")
      .upload(tempPath, uploadFile);

    if (uploadError) {
      console.error("[useDocumentLink] Temp upload error:", uploadError);
      return { validFrom: null, expiresAt: null, issueDate: null };
    }

    try {
      const extracted = await extractDatesFromStorageFile({
        filePath: tempPath,
        fileName: uploadFile.name,
        mimeType: uploadFile.type,
      });
      return extracted;
    } finally {
      // Best-effort cleanup
      await supabase.storage.from("documents").remove([tempPath]);
    }
  }, [user?.id, extractDatesFromStorageFile]);

  // Effect to extract dates when file changes
  useEffect(() => {
    if (!file || !user?.id) {
      setIsExtractingDates(false);
      setDatesExtracted(false);
      return;
    }

    // Cancel any ongoing extraction
    if (extractionAbortRef.current) {
      extractionAbortRef.current.abort();
    }

    const controller = new AbortController();
    extractionAbortRef.current = controller;

    const doExtraction = async () => {
      setIsExtractingDates(true);
      setDatesExtracted(false);

      try {
        const extracted = await extractDatesFromFile(file);
        
        // Check if aborted
        if (controller.signal.aborted) return;

        // Update dates if found
        if (extracted.validFrom) {
          setValidFrom(extracted.validFrom);
        }
        if (extracted.expiresAt) {
          setExpiresAt(extracted.expiresAt);
        }
        
        // Show toast if dates were found
        if (extracted.validFrom || extracted.expiresAt) {
          toast({
            title: "Dates détectées",
            description: "Les dates ont été pré-remplies à partir du document",
          });
        }

        setDatesExtracted(true);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Extraction error:", error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsExtractingDates(false);
        }
      }
    };

    doExtraction();

    return () => {
      controller.abort();
    };
  }, [file, user?.id, extractDatesFromFile]);

  // Pre-fill / extract dates when selecting an existing document
  useEffect(() => {
    if (!selectedDoc) {
      setIsExtractingLinkDates(false);
      setLinkDatesExtracted(false);
      return;
    }

    // 1) If the document already has dates stored, use them immediately
    if (selectedDoc.expires_at) {
      const expiryDate = new Date(selectedDoc.expires_at).toISOString().split("T")[0];
      setExpiresAt(expiryDate);
    }
    if (selectedDoc.doc_date) {
      const docDate = new Date(selectedDoc.doc_date).toISOString().split("T")[0];
      setValidFrom(docDate);
    }

    // 2) If missing, run AI extraction on the existing stored file
    const shouldExtract = !selectedDoc.expires_at && !selectedDoc.doc_date;
    if (!shouldExtract) {
      setLinkDatesExtracted(true);
      return;
    }

    const controller = new AbortController();

    const run = async () => {
      setIsExtractingLinkDates(true);
      setLinkDatesExtracted(false);

      const extracted = await extractDatesFromStorageFile({
        filePath: selectedDoc.file_path,
        fileName: selectedDoc.file_name,
        mimeType: selectedDoc.mime_type || "application/pdf",
      });

      if (controller.signal.aborted) return;

      if (extracted.validFrom) setValidFrom(extracted.validFrom);
      if (extracted.expiresAt) setExpiresAt(extracted.expiresAt);

      if (extracted.validFrom || extracted.expiresAt) {
        toast({
          title: "Dates détectées",
          description: "Dates pré-remplies à partir du document sélectionné",
        });
      }

      setLinkDatesExtracted(true);
      setIsExtractingLinkDates(false);
    };

    run().catch((e) => {
      if (!controller.signal.aborted) {
        console.error("[useDocumentLink] Link date extraction failed:", e);
        setIsExtractingLinkDates(false);
      }
    });

    return () => controller.abort();
  }, [selectedDoc, extractDatesFromStorageFile]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSelectedDoc(null);
      setFile(null);
      setExpiresAt("");
      setValidFrom("");
      setIsExtractingDates(false);
      setDatesExtracted(false);
      setIsExtractingLinkDates(false);
      setLinkDatesExtracted(false);
      
      // Auto-calculate expiration date if validityDays is set
      if (docConfig.validityDays) {
        const now = new Date();
        setValidFrom(now.toISOString().split("T")[0]);
        const expiry = new Date(now);
        expiry.setDate(expiry.getDate() + docConfig.validityDays);
        setExpiresAt(expiry.toISOString().split("T")[0]);
      }
    } else {
      // Cancel extraction on close
      if (extractionAbortRef.current) {
        extractionAbortRef.current.abort();
      }
    }
  }, [open, docConfig.validityDays]);

  // Fetch existing documents for linking
  const { data: documents = [], isLoading: loadingDocs } = useQuery({
    queryKey: ["documents-for-link", user?.id, searchQuery],
    queryFn: async () => {
      if (!user?.id) return [];

      let query = supabase
        .from("documents")
        .select("id, title, file_name, file_path, mime_type, bucket_id, doc_type, issuer_name, doc_date, expires_at, ai_summary, category, key_fields, created_at")
        .eq("user_id", user.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(100);

      if (searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery}%,file_name.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ExistingDocument[];
    },
    enabled: open && !!user?.id,
  });

  // Link existing document mutation
  const linkMutation = useMutation({
    mutationFn: async (documentId: string) => {
      if (!user?.id) throw new Error("Non authentifié");

      // Handle upsert for health (essential_doc_links)
      if (linkTableName === "essential_doc_links") {
        // Build the upsert data
        const upsertData: {
          user_id: string;
          category_key: string;
          document_id: string;
          family_member_id?: string | null;
        } = {
          user_id: user.id,
          category_key: docConfig.key,
          document_id: documentId,
        };

        // Add family_member_id if provided
        if (familyMemberId) {
          upsertData.family_member_id = familyMemberId;

          // For family members, we need to check and upsert differently
          const { data: existing } = await supabase
            .from("essential_doc_links")
            .select("id")
            .eq("user_id", user.id)
            .eq("category_key", docConfig.key)
            .eq("family_member_id", familyMemberId)
            .maybeSingle();

          if (existing) {
            const { error } = await supabase
              .from("essential_doc_links")
              .update({ document_id: documentId })
              .eq("id", existing.id);
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from("essential_doc_links")
              .insert(upsertData);
            if (error) throw error;
          }
        } else {
          // Original logic for user's own documents
          const { error } = await supabase
            .from("essential_doc_links")
            .upsert({
              user_id: user.id,
              category_key: docConfig.key,
              document_id: documentId,
            }, {
              onConflict: "user_id,category_key",
            });
          if (error) throw error;
        }
      } else if (linkTableName === "vehicle_documents") {
        // Use RPC to avoid enum vs text comparison issues
        // Cast to any to bypass TypeScript enum typing - the DB function handles the enum correctly
        const { error } = await supabase.rpc("upsert_vehicle_document_link", {
          p_vehicle_id: entityId,
          p_doc_type: docConfig.key as any,
          p_document_id: documentId,
          p_valid_from: validFrom || null,
          p_expires_at: expiresAt || null,
          p_status: "valid" as any,
          p_label: docConfig.label || null,
        });
        if (error) throw error;
      } else if (linkTableName === "property_documents") {
        // Handle property documents
        const { data: existing } = await supabase
          .from("property_documents")
          .select("id")
          .eq("property_id", entityId)
          .eq("doc_type", docConfig.key)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("property_documents")
            .update({ 
              document_id: documentId, 
              expires_at: expiresAt || null, 
              valid_from: validFrom || null, 
              status: "valid" 
            })
            .eq("id", existing.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("property_documents")
            .insert({
              property_id: entityId,
              doc_type: docConfig.key,
              document_id: documentId,
              expires_at: expiresAt || null,
              valid_from: validFrom || null,
              status: "missing",
            });
          if (error) throw error;
        }
      } else if (linkTableName === "subcontractor_documents") {
        // Handle subcontractor documents
        const { data: existing } = await supabase
          .from("subcontractor_documents")
          .select("id")
          .eq("subcontractor_id", entityId)
          .eq("document_type", docConfig.key as any)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("subcontractor_documents")
            .update({ 
              document_id: documentId, 
              expires_at: expiresAt || null, 
              valid_from: validFrom || null, 
              status: "valid" 
            })
            .eq("id", existing.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("subcontractor_documents")
            .insert({
              subcontractor_id: entityId,
              document_type: docConfig.key as any,
              document_id: documentId,
              expires_at: expiresAt || null,
              valid_from: validFrom || null,
              status: "valid",
            });
          if (error) throw error;
        }
      } else if (linkTableName === "legal_documents") {
        // Handle legal documents - entityId is company_id
        const { data: existing } = await supabase
          .from("legal_documents")
          .select("id")
          .eq("company_id", entityId)
          .eq("document_type", docConfig.key as any)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("legal_documents")
            .update({ 
              document_id: documentId, 
              expires_at: expiresAt || null, 
              valid_from: validFrom || null, 
              status: "valid" 
            })
            .eq("id", existing.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("legal_documents")
            .insert({
              user_id: user.id,
              company_id: entityId,
              document_type: docConfig.key as any,
              document_id: documentId,
              expires_at: expiresAt || null,
              valid_from: validFrom || null,
              status: "valid",
            });
          if (error) throw error;
        }
      } else if (linkTableName === "drivers") {
        // Handle driver license - direct column update
        const { error } = await supabase
          .from("drivers")
          .update({ 
            license_document_id: documentId,
            license_expiry: expiresAt || null,
          })
          .eq("id", entityId);
        if (error) throw error;
      } else if (linkTableName === "demarche_documents") {
        // Handle demarche documents - entityId is the demarche_document row id
        const { error } = await supabase
          .from("demarche_documents")
          .update({ 
            document_id: documentId,
            is_completed: true,
          })
          .eq("id", entityId);
        if (error) throw error;
      }

      // Optionally update expiration on the document itself
      if (expiresAt) {
        await supabase
          .from("documents")
          .update({ expires_at: expiresAt })
          .eq("id", documentId);
      }
    },
    onSuccess: () => {
      toast({
        title: "Document lié",
        description: `Le document a été lié à "${docConfig.label}"`,
      });
      
      // Invalidate the correct query keys based on entity type
      if (entityType === "vehicle") {
        queryClient.invalidateQueries({ queryKey: ["vehicle-documents", entityId] });
        queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      } else if (entityType === "property") {
        queryClient.invalidateQueries({ queryKey: ["property-documents", entityId] });
        queryClient.invalidateQueries({ queryKey: ["properties"] });
      } else if (entityType === "subcontractor") {
        queryClient.invalidateQueries({ queryKey: ["subcontractor-documents", entityId] });
        queryClient.invalidateQueries({ queryKey: ["subcontractors"] });
      } else if (entityType === "health") {
        queryClient.invalidateQueries({ queryKey: ["essential-doc-links"] });
      } else if (entityType === "legal") {
        queryClient.invalidateQueries({ queryKey: ["legal-documents"] });
      } else if (entityType === "driver") {
        queryClient.invalidateQueries({ queryKey: ["driver", entityId] });
        queryClient.invalidateQueries({ queryKey: ["drivers"] });
      } else if (entityType === "demarche_document") {
        queryClient.invalidateQueries({ queryKey: ["demarche"] });
        queryClient.invalidateQueries({ queryKey: ["demarches"] });
      }
      
      queryClient.invalidateQueries({ queryKey: ["compliance"] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Link error:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de lier le document",
        variant: "destructive",
      });
    },
  });

  // Upload and link mutation
  const uploadMutation = useMutation({
    mutationFn: async (uploadFile: File) => {
      if (!user?.id) throw new Error("Non authentifié");

      // 1. Upload file to storage
      const fileExt = uploadFile.name.split(".").pop();
      const filePath = `${user.id}/${config.storagePath}/${docConfig.key}_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(filePath, uploadFile);

      if (uploadError) throw uploadError;

      // 2. Create document entry
      const { data: newDoc, error: docError } = await supabase
        .from("documents")
        .insert({
          user_id: user.id,
          title: docConfig.label,
          file_name: uploadFile.name,
          file_path: filePath,
          file_size: uploadFile.size,
          mime_type: uploadFile.type,
          doc_type: (docConfig.doc_type || "other") as DocumentType,
          keywords: docConfig.keywords || [],
          expires_at: expiresAt || null,
          doc_date: validFrom || null,
          source: "upload",
          status: "complete",
        })
        .select("id")
        .single();

      if (docError) throw docError;

      // 3. Create link
      await linkMutation.mutateAsync(newDoc.id);

      return newDoc;
    },
    onError: (error: Error) => {
      console.error("Upload error:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'importer le document",
        variant: "destructive",
      });
    },
  });

  const handleLink = useCallback(async () => {
    if (!selectedDoc) return;
    await linkMutation.mutateAsync(selectedDoc.id);
  }, [selectedDoc, linkMutation]);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    await uploadMutation.mutateAsync(file);
  }, [file, uploadMutation]);

  const clearFile = useCallback(() => {
    setFile(null);
  }, []);

  return {
    // State
    searchQuery,
    setSearchQuery,
    selectedDoc,
    setSelectedDoc,
    file,
    setFile,
    expiresAt,
    setExpiresAt,
    validFrom,
    setValidFrom,
    
    // Data
    documents,
    loadingDocs,
    
    // Actions
    handleLink,
    handleUpload,
    clearFile,
    
    // Loading states
    isLinking: linkMutation.isPending,
    isUploading: uploadMutation.isPending,
    isExtractingDates,
    datesExtracted,
    isExtractingLinkDates,
    linkDatesExtracted,
  };
}
